import { type ComponentType } from 'react';

import type { BreadcrumbConfig } from '../ui-components/page/PageHeader';

import Demo from '../modules/common/Demo';
import StaticPages from '../modules/content/staticPage/StaticPage';
import StaticPageForm from '../modules/content/staticPage/StaticPageForm';
import DashboardPage from '../modules/dashboard/DashboardPage';
import SystemEmailModalRoute from '../modules/content/systemEmails/SystemEmailModalRoute';
import SystemEmailPage from '../modules/content/systemEmails/SystemEmailPage';
import { MenuDragDrop } from '../modules/dynamicMenu/MenuDragDrop';
import LogsPage from '../modules/errorLogs/LogsPage';
import ChangePasswordPage from '../modules/home/changePassword/ChangePasswordPage';
import EditProfilePage from '../modules/home/editProfile/EditProfilePage';
import HBTemplatesPage from '../modules/hbTemplates/HBTemplatesPage';
import Countries from '../modules/region/country/Countries';
import CountriesModalRoute from '../modules/region/country/CountriesModalRoute';
import StatesModalRoute from '../modules/region/states/StatesModalRoute';
import States from '../modules/region/states/States';
import PermissionForm from '../modules/roleManagement/permissions/PermissionForm';
import PermissionsPage from '../modules/roleManagement/permissions/PermissionsPage';
import RoleData from '../modules/roleManagement/role/RoleData';
import RolePage from '../modules/roleManagement/role/RolePage';
import FilesSettingPage from '../modules/settings/FilesSettingPage';
import SettingsPage from '../modules/settings/SettingsPage';
import ConfigurationsSettingPage from '../modules/settings/ConfigurationsSettingPage';
import CompanySettingPage from '../modules/settings/CompanySettingPage';
import EmailNotificationsPage from '../modules/configurations/emailNotifications/EmailNotificationsPage';
import SystemNotificationsPage from '../modules/configurations/systemNotifications/SystemNotificationsPage';
import PushNotificationsPage from '../modules/configurations/pushNotifications/PushNotificationsPage';
import SystemSettingsPage from '../modules/configurations/systemSettings/SystemSettingsPage';
import NotificationsPage from '../modules/notifications/NotificationsPage';
import Sitemap from '../modules/sitemap/SiteMapPage';
import AdminsPage from '../modules/userManagement/admin/AdminsPage';
import AdminsModalRoute from '../modules/userManagement/admin/AdminsModalRoute';
import UsersPage from '../modules/userManagement/users/UsersPage';
import UserDetailPage from '../modules/userManagement/users/UserDetailPage';
import DeletedUsersPage from '../modules/userManagement/users/DeletedUsersPage';
import DeletedUserDetailPage from '../modules/userManagement/users/DeletedUserDetailPage';
import EventsPage from '../modules/eventManagement/events/EventsPage';
import EventDetailPage from '../modules/eventManagement/events/EventDetailPage';
export interface RouteMeta {
  permission?: string[] | string;
  section?: string;
  sectionPath?: string;
  underConstruction?: boolean;
}
export interface AppRouteConfig {
  path: string;
  title: string;
  element: ComponentType;
  meta: RouteMeta;
  breadcrumbs?: BreadcrumbConfig[];
}

const protectedRoutes: AppRouteConfig[] = [
  {
    path: '/demo',
    title: 'Demo',
    element: Demo,
    meta: {
      permission: [],
    },
  },
  {
    path: '/dashboard',
    title: 'Dashboard',
    element: DashboardPage,
    meta: {
      permission: [],
    },
  },
  {
    path: '/notifications',
    title: 'Notifications',
    element: NotificationsPage,
    meta: {
      permission: [],
      section: 'Notifications',
      sectionPath: '/notifications',
    },
    breadcrumbs: [
      { label: 'Home', to: '/dashboard' },
      { label: 'Notifications', current: true },
    ],
  },
  {
    path: '/menu',
    title: 'Menu Builder',
    element: MenuDragDrop,
    meta: {
      permission: [
        'admin_menu_add',
        'admin_menu_edit',
        'admin_menu_update',
        'admin_menu_view',
        'admin_menu_delete',
        'admin_menu_list',
      ],
      section: 'System & Access',
      sectionPath: '/app-settings/company',
    },
  },
  {
    path: '/logs',
    title: 'Logs',
    element: LogsPage,
    meta: {
      permission: ['activity_list'],
      section: 'System & Access',
      sectionPath: '/app-settings/company',
    },
  },
  {
    path: '/sitemap',
    title: 'Sitemap',
    element: Sitemap,
    meta: {
      permission: [],
    },
  },
  {
    path: '/profile',
    title: 'Edit Profile',
    element: EditProfilePage,
    meta: {
      permission: ['profile_view', 'profile_edit', 'profile_update'],
    },
  },
  {
    path: '/change-password',
    title: 'Change Password',
    element: ChangePasswordPage,
    meta: {
      permission: ['profile_change_password'],
    },
  },
  {
    path: '/my-profile',
    title: 'My Profile',
    element: EditProfilePage,
    meta: {
      permission: [],
      section: 'Profile',
      sectionPath: '/my-profile',
    },
  },
  {
    path: '/hb-templates',
    title: 'HB Templates',
    element: HBTemplatesPage,
    meta: {
      permission: [],
    },
  },
  {
    path: '/admins',
    title: 'Admins',
    element: AdminsPage,
    meta: {
      permission: ['admin_list'],
      section: 'User Management',
      sectionPath: '/admins',
    },
  },
  {
    path: '/admins/create',
    title: 'Create Admin',
    element: AdminsModalRoute,
    meta: {
      permission: ['admin_add'],
      section: 'User Management',
      sectionPath: '/admins',
    },
  },
  {
    path: '/admins/:id',
    title: 'Edit Admin',
    element: AdminsModalRoute,
    meta: {
      permission: ['admin_edit', 'admin_update'],
      section: 'User Management',
      sectionPath: '/admins',
    },
  },
  {
    path: '/users/list',
    title: 'Users',
    element: UsersPage,
    meta: {
      permission: [],
      section: 'User Management',
      sectionPath: '/users/list',
    },
    breadcrumbs: [
      { label: 'User Management' },
      { label: 'Users', current: true },
    ],
  },
  {
    path: '/users/view/:userId',
    title: 'User Details',
    element: UserDetailPage,
    meta: {
      permission: [],
      section: 'User Management',
      sectionPath: '/users/list',
    },
    breadcrumbs: [
      { label: 'User Management' },
      { label: 'Users', to: '/users/list' },
      { label: 'User Details', current: true },
    ],
  },
  {
    path: '/users/deleted',
    title: 'Deleted Users',
    element: DeletedUsersPage,
    meta: {
      permission: [],
      section: 'User Management',
      sectionPath: '/users/list',
    },
    breadcrumbs: [
      { label: 'User Management' },
      { label: 'Deleted Users', current: true },
    ],
  },
  {
    path: '/users/deleted/:userId',
    title: 'Deleted User Details',
    element: DeletedUserDetailPage,
    meta: {
      permission: [],
      section: 'User Management',
      sectionPath: '/users/list',
    },
    breadcrumbs: [
      { label: 'User Management' },
      { label: 'Deleted Users', to: '/users/deleted' },
      { label: 'Deleted User Details', current: true },
    ],
  },
  {
    path: '/events',
    title: 'Event Management',
    element: EventsPage,
    meta: {
      permission: [],
      section: 'Event Management',
      sectionPath: '/events',
    },
    breadcrumbs: [{ label: 'Event Management', current: true }],
  },
  {
    path: '/events/view/:id',
    title: 'Event Details',
    element: EventDetailPage,
    meta: {
      permission: [],
      section: 'Event Management',
      sectionPath: '/events',
    },
    breadcrumbs: [
      { label: 'Event Management', to: '/events' },
      { label: 'Event Details', current: true },
    ],
  },
  {
    path: '/roles',
    title: 'Roles',
    element: RolePage,
    meta: {
      permission: ['group_list'],
      section: 'User Management',
      sectionPath: '/admins',
    },
  },
  {
    path: '/roles/:id',
    title: 'Role Details',
    element: RoleData,
    meta: {
      permission: ['group_view'],
      section: 'User Management',
      sectionPath: '/admins',
    },
  },
  {
    path: '/permissions',
    title: 'Permissions',
    element: PermissionsPage,
    meta: {
      permission: ['permission_list'],
      section: 'User Management',
      sectionPath: '/admins',
    },
  },
  {
    path: '/permissions/:option',
    title: 'Permission Form',
    element: PermissionForm,
    meta: {
      permission: ['permission_add', 'permission_edit', 'permission_update'],
      section: 'User Management',
      sectionPath: '/admins',
    },
  },
  {
    path: '/email',
    title: 'System Emails',
    element: SystemEmailPage,
    meta: {
      permission: ['system_emails_list'],
      section: 'Content',
      sectionPath: '/pages',
    },
  },
  {
    path: '/email/create',
    title: 'Create Email',
    element: SystemEmailModalRoute,
    meta: {
      permission: ['system_emails_add'],
      section: 'Content',
      sectionPath: '/pages',
    },
  },
  {
    path: '/email/:id',
    title: 'Edit Email',
    element: SystemEmailModalRoute,
    meta: {
      permission: ['system_emails_edit', 'system_emails_update'],
      section: 'Content',
      sectionPath: '/pages',
    },
  },
  {
    path: '/configurations/static-pages',
    title: 'Static Pages',
    element: StaticPages,
    meta: {
      permission: [],
      section: 'Content',
      sectionPath: '/configurations/static-pages',
    },
  },
  {
    path: '/configurations/static-pages/create',
    title: 'Create Page',
    element: StaticPageForm,
    meta: {
      permission: [],
      section: 'Content',
      sectionPath: '/configurations/static-pages',
    },
  },
  {
    path: '/configurations/static-pages/:id',
    title: 'Edit Page',
    element: StaticPageForm,
    meta: {
      permission: [],
      section: 'Content',
      sectionPath: '/configurations/static-pages',
    },
  },
  {
    path: '/countries',
    title: 'Countries',
    element: Countries,
    meta: {
      permission: ['country_list'],
      section: 'Region',
      sectionPath: '/countries',
    },
  },
  {
    path: '/countries/create',
    title: 'Add',
    element: CountriesModalRoute,
    meta: {
      permission: ['country_add'],
      section: 'Region',
      sectionPath: '/countries',
    },
  },
  {
    path: '/countries/:id',
    title: 'Edit',
    element: CountriesModalRoute,
    meta: {
      permission: ['country_edit', 'country_update'],
      section: 'Region',
      sectionPath: '/countries',
    },
  },
  {
    path: '/states',
    title: 'States',
    element: States,
    meta: {
      permission: ['state_list'],
      section: 'Region',
      sectionPath: '/countries',
    },
  },
  {
    path: '/states/create',
    title: 'Add',
    element: StatesModalRoute,
    meta: {
      permission: ['state_add'],
      section: 'Region',
      sectionPath: '/countries',
    },
  },
  {
    path: '/states/:id',
    title: 'Edit',
    element: StatesModalRoute,
    meta: {
      permission: ['state_edit', 'state_update'],
      section: 'Region',
      sectionPath: '/countries',
    },
  },
  {
    path: '/app-settings/company',
    title: 'Company Settings',
    element: CompanySettingPage,
    meta: {
      permission: 'settings_company_view',
      section: 'System & Access',
      sectionPath: '/app-settings/company',
    },
  },
  {
    path: '/app-settings/configurations',
    title: 'Configurations',
    element: ConfigurationsSettingPage,
    meta: {
      permission: ['settings_config_view', 'settings_config_list'],
      section: 'System & Access',
      sectionPath: '/app-settings/company',
    },
  },
  {
    path: '/configurations/email-notifications',
    title: 'Email Notifications',
    element: EmailNotificationsPage,
    meta: {
      permission: [],
      section: 'System & Access',
      sectionPath: '/app-settings/company',
    },
    breadcrumbs: [
      { label: 'Configurations' },
      { label: 'Email Notifications', current: true },
    ],
  },
  {
    path: '/configurations/system-notifications',
    title: 'System Notifications',
    element: SystemNotificationsPage,
    meta: {
      permission: [],
      section: 'System & Access',
      sectionPath: '/app-settings/company',
    },
    breadcrumbs: [
      { label: 'Configurations' },
      { label: 'System Notifications', current: true },
    ],
  },
  {
    path: '/configurations/push-notifications',
    title: 'Push Notifications',
    element: PushNotificationsPage,
    meta: {
      permission: [],
      section: 'System & Access',
      sectionPath: '/app-settings/company',
    },
    breadcrumbs: [
      { label: 'Configurations' },
      { label: 'Push Notifications', current: true },
    ],
  },
  {
    path: '/configurations/system-settings',
    title: 'System Settings',
    element: SystemSettingsPage,
    meta: {
      permission: [],
      section: 'System & Access',
      sectionPath: '/app-settings/company',
    },
    breadcrumbs: [
      { label: 'Home', to: '/dashboard' },
      { label: 'Configurations' },
      { label: 'System Settings', current: true },
    ],
  },
  {
    path: '/app-settings/:category',
    title: 'App Settings',
    element: SettingsPage,
    meta: {
      permission: [
        'settings_company_view',
        'settings_company_update',
        'settings_config_list',
        'settings_config_view',
        'settings_config_update',
        'settings_config_edit',
        'settings_preferences_view',
        'settings_preferences_update',
        'settings_sms_view',
        'settings_sms_update',
        'settings_email_view',
        'settings_email_update',
        'settings_authenticate_view',
        'settings_authenticate_update',
        'settings_appearance_view',
        'settings_appearance_update',
      ],
      section: 'System & Access',
      sectionPath: '/app-settings/company',
    },
  },
  {
    path: '/app-settings/files',
    title: 'Files Setting',
    element: FilesSettingPage,
    meta: {
      permission: ['settings_file_view', 'settings_file_update'],
      section: 'System & Access',
      sectionPath: '/app-settings/company',
    },
  },
];

export default protectedRoutes;
